import {AxisConverter, ConfigGraph, Graph, GraphFactory, IcolorGraph, UnitConverter} from '../../models/graph';
import {NoResults} from '../../models/no-results';

export class GenericChartModel {

  graphData: Graph;
  chartData;
  buildData;
  configData: ConfigGraph;
  legendData = <any>{};
  loading = true;
  noResults: NoResults;

  static sortDays(d1, d2, d3, d4) {
    let ret = 0;
    if (d3 < d4) {
      ret = -1;
    } else if (d3 > d4) {
      ret = 1;
    } else {
      if (d1 > d2) {
        ret = 1;
      } else if (d1 < d2) {
        ret = -1;
      } else {
        ret = 0;
      }
    }
    return ret;
  }

  static sortByAttribute(d1, d2) {
    if (d1 > d2) {
      return 1;
    } else if (d1 < d2) {
      return -1;
    } else {
      return 0;
    }
  }

  static getHumanReadableDateYearMonthDay(timestamp) {
    const date = new Date(timestamp);
    return (
      date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
    );
  }

  static sortScore(dataSet, attr = 'key') {
    dataSet.sort((t1, t2) => {
      let d1, d2, d3, d4, da, db: any;
      if (attr === 'day') {
        da = t1[attr].split('/');
        db = t2[attr].split('/');
        [d1, d3] = [parseInt(da[0], 10), parseInt(da[1], 10)];
        [d2, d4] = [parseInt(db[0], 10), parseInt(db[1], 10)];
        return this.sortDays(d1, d2, d3, d4);
      } else {
        d1 = t1[attr];
        d2 = t2[attr];
        return GenericChartModel.sortByAttribute(d1, d2);
      }
    });
  }

  static isLogarithmic(values) {
    let maxValue = 0;
    let minValue = Infinity;

    values.forEach(value => {
      if (maxValue < value) {
        maxValue = value;
      }
      if (minValue > value) {
        minValue = value;
      }
    });
    if (maxValue / minValue >= 100) {
      return true;
    }
    return false;
  }

  static formatValue(value, formattedValue, valueAxis) {
    return value + (valueAxis.unit || '');
  }

  static labelTextFormat(item, value) {
    const columnWidth = item.columnWidth;
    if (columnWidth <= 20) {
      return '';
    } else {
      return value;
    }
  }

  static labelCategoryAxis(label, item, axis) {
    const chartTemp = axis.chart;
    if ((chartTemp.realWidth <= 300) && (label.length > 5)) {
      return label.substr(0, 5) + '...';
    }
    if ((chartTemp.realWidth <= 500) && (label.length > 10)) {
      return label.substr(0, 10) + '...';
    }
    return label;
  }

  constructor() {
  }

  setBuildData() {
    const data: any = {...this.configData, ...this.chartData, legendData: this.legendData};
    const tempBuildData = <any>{
      type: 'serial',
      hideCredits: true,
      theme: 'light',
      marginTop: data.marginTop,
      marginBottom: data.marginBottom,
      marginRight: data.marginRight,
      marginLeft: data.marginLeft,
      valueAxes: [
        {
          logarithmic: data.valueAxis && data.valueAxis.logarithmic ? data.valueAxis.logarithmic : false,
          axisAlpha: data.valueAxis && data.valueAxis.axisAlpha ? data.valueAxis.axisAlpha : 0,
          gridAlpha: data.valueAxis && data.valueAxis.gridAlpha ? data.valueAxis.gridAlpha : 0,
          labelsEnabled: data.valueAxis && data.valueAxis.labelsEnabled ? data.valueAxis.labelsEnabled : false,
          unit: data.valueAxis && data.valueAxis.unit ? data.valueAxis.unit : '',
          unitPosition: data.valueAxis && data.valueAxis.unitPosition ? data.valueAxis.unitPosition : 'right',
          usePrefixes: true,
          precision: 0
        }
      ],
      categoryField: data.categoryField,
      categoryAxis: {
        axisAlpha: data.categoryAxis && data.categoryAxis.axisAlpha ? data.categoryAxis.axisAlpha : 0,
        gridAlpha: data.categoryAxis && data.categoryAxis.gridAlpha ? data.categoryAxis.gridAlpha : 0,
        labelsEnabled: data.categoryAxis && data.categoryAxis.labelsEnabled ? data.categoryAxis.labelsEnabled : false,
        labelRotation: data.categoryAxis && data.categoryAxis.labelRotation ? data.categoryAxis.labelRotation : 0,
      }
    };

    tempBuildData.dataProvider = data.data;
    tempBuildData.graphs = data.graph;
    tempBuildData.valueAxes[0].labelFunction = this.getConverterAxis();

    if (data.categoryAxis && !data.categoryAxis.labelsColor) {
      tempBuildData.categoryAxis.color = 'transparent';
    }

    if (data.valueAxis && data.valueAxis.minimum != null) {
      tempBuildData.valueAxes[0].minimum = data.valueAxis.minimum;
    }

    if (data.valueAxis && data.valueAxis.maximum != null) {
      tempBuildData.valueAxes[0].maximum = data.valueAxis.maximum;
    }

    if (data.valueAxis && data.valueAxis.precision != null) {
      tempBuildData.valueAxes[0].precision = data.valueAxis.precision;
    }

    if (data.chartCursor) {
      tempBuildData.chartCursor = {};
    }
    if (data.balloonPointerOrientation) {
      tempBuildData.chartCursor = {
        valueLineEnabled: false,
        valueLineAlpha: 0,
        cursorColor: 'transparent',
        balloonPointerOrientation: 'horizontal'
      };
    }

    if (data.valueScrollbar) {
      tempBuildData.valueScrollbar = {
        oppositeAxis: false,
        offset: 50,
        scrollbarHeight: 20,
        dragIconWidth: 30,
        dragIconHeight: 30,
        backgroundAlpha: 0,
        selectedBackgroundAlpha: 0.1,
        selectedBackgroundColor: '#888888',
        graphFillAlpha: 0,
        graphLineAlpha: 0.5,
        selectedGraphFillAlpha: 0,
        selectedGraphLineAlpha: 1,
        autoGridCount: true
      };
    }

    if (data.chartScrollbar) {
      tempBuildData.chartScrollbar = {
        oppositeAxis: false,
        offset: 40,
        scrollbarHeight: 20,
        dragIconWidth: 30,
        dragIconHeight: 30,
        backgroundAlpha: 0,
        selectedBackgroundAlpha: 0.1,
        selectedBackgroundColor: '#888888',
        graphFillAlpha: 0,
        graphLineAlpha: 0.5,
        selectedGraphFillAlpha: 0,
        selectedGraphLineAlpha: 1,
        autoGridCount: true
      };
    }

    if (data.isLegend && data.legendData && Object.keys(data.legendData).length) {
      tempBuildData.legend = data.legendData;
    }

    this.buildData = {...data, buildData: tempBuildData};
  }

  setLoading(booleanCondition) {
    this.loading = booleanCondition;
  }

  setError(noResults: NoResults) {
    this.noResults = {...noResults};
    this.noResults.isError = true;
  }

  setGraphDataVariable(graphData: Graph) {
    this.graphData = GraphFactory.graphicCopy(graphData);
  }

  setData(config: ConfigGraph) {
    this.configData = {...config};
  }

  getData(seriesArg) {
  }

  getConverter(value) {
    return UnitConverter[this.configData.unitConverterType].bind(UnitConverter)(value, this.configData.valueAxis ?
      this.configData.valueAxis.unit :
      '');
  }

  getConverterAxis() {
    return AxisConverter[this.configData.valueAxisConverterType].bind(AxisConverter);
  }
}

